import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LogOut, UserRound, Ticket, LayoutDashboard, Radar, Map, Menu, BusFront, BarChart3, Wallet, KeyRound, MonitorPlay } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { BrandLockup, QuickRideLockup, FlagStrip } from "@/components/Brand";
import { useAuth, primaryRole, userRoles } from "@/context/AuthContext";
import { useStations } from "@/components/SearchForm";
import { ROLE_THEME } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";

const STAFF_LABEL = { admin: "Admin Console", teller: "Teller Counter", cashier: "Cashier Office", station_master: "Station Console", manager: "Management", bus_owner: "Bus Owner" };

const MODULE_META = {
  "/": { id: "book", label: "Book a trip", icon: BusFront, end: true },
  "/find": { id: "find-ticket", label: "My ticket", icon: Ticket },
  "/live": { id: "live", label: "Live map", icon: Map },
  "/arrivals": { id: "arrivals", label: "Arrivals board", icon: MonitorPlay },
  "/account": { id: "account", label: "My account", icon: UserRound },
  "/admin": { id: "admin", label: "Admin Console", icon: LayoutDashboard },
  "/station": { id: "station", label: "Station Console", icon: LayoutDashboard },
  "/management": { id: "management", label: "Management", icon: BarChart3 },
  "/control": { id: "control", label: "Control room", icon: Radar },
  "/cashier": { id: "cashier", label: "Cashier Office", icon: Wallet },
  "/teller": { id: "teller", label: "Teller Counter", icon: Ticket },
};
const ORDER = ["/", "/find", "/live", "/account", "/admin", "/station", "/management", "/control", "/cashier", "/teller", "/arrivals"];
const CUSTOMER_PUBLIC = ["/find"];
const CUSTOMER_MODULES = ["/find", "/account"];
const STAFF_MODULES = {
  admin: ["/admin", "/management", "/control", "/arrivals", "/cashier"],
  station_master: ["/station", "/arrivals", "/management", "/control"],
  manager: ["/management", "/control"],
  teller: ["/teller"],
  cashier: ["/cashier"],
  bus_owner: ["/account"],
};

function navItems(area, user) {
  if (area === "staff") {
    if (!user) return [];
    const set = new Set(userRoles(user).flatMap((r) => STAFF_MODULES[r] || []));
    return ORDER.filter((p) => set.has(p)).map((p) => ({ to: p, ...MODULE_META[p] }));
  }
  return (user ? CUSTOMER_MODULES : CUSTOMER_PUBLIC).map((p) => ({ to: p, ...MODULE_META[p] }));
}

function MenuSheet({ items, user, terminal, onChangePassword, always }) {
  const [open, setOpen] = useState(false);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="sm" className={`rounded-full ${always ? "" : "lg:hidden"}`} data-testid="mobile-menu-button" aria-label="Open menu">
          <Menu className="h-5 w-5" />{always && <span className="ml-1 hidden font-semibold sm:inline">Menu</span>}
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-72" data-testid="mobile-menu">
        <SheetTitle className="font-display text-[#182678]">Menu</SheetTitle>
        {user && (
          <div className="mt-3 rounded-xl bg-slate-50 p-3" data-testid="mobile-identity">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><UserRound className="h-4 w-4 text-[#0052FF]" />{user.name}</div>
            {terminal && <div className="mt-1 text-xs text-slate-500" data-testid="mobile-terminal">{terminal.city} · {terminal.name}</div>}
          </div>
        )}
        <div className="mt-4 flex flex-col gap-1">
          {items.map((i) => (
            <NavLink key={i.id} to={i.to} end={i.end} onClick={() => setOpen(false)} data-testid={`mobile-nav-${i.id}-link`}
              className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-semibold transition-colors ${isActive ? "bg-blue-50 text-[#0052FF]" : "text-slate-700 hover:bg-slate-50"}`}>
              <i.icon className="h-4 w-4" />{i.label}
            </NavLink>
          ))}
          {user && onChangePassword && (
            <button onClick={() => { setOpen(false); onChangePassword(); }} data-testid="mobile-change-password-link"
              className="flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50">
              <KeyRound className="h-4 w-4" />Change password
            </button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function Header() {
  const { user, signOut, area } = useAuth();
  const nav = useNavigate();
  const stations = useStations();
  const [pwOpen, setPwOpen] = useState(false);
  const isStaffArea = area === "staff";
  const role = primaryRole(user);
  const terminal = isStaffArea && user?.station_id ? stations.find((s) => s.id === user.station_id) : null;
  const items = navItems(area, user);
  const logoutDest = isStaffArea ? "/admin" : "/";

  return (
    <header className="no-print sticky top-0 z-40 border-b border-slate-200/80 bg-white/95 backdrop-blur-md">
      <FlagStrip className="h-[3px]" />
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-3 px-4 sm:px-6">
        {isStaffArea
          ? <BrandLockup sub={STAFF_LABEL[role] || "Staff Console"} subClass={ROLE_THEME[role]?.text || "text-[#0284C7]"} />
          : <QuickRideLockup />}
        <nav className="flex items-center gap-1 sm:gap-2">
          {!isStaffArea && (
            <Button size="sm" aria-label="Book a trip" className="rounded-full bg-[#0052FF] hover:bg-[#0041CC] btn-press px-4 font-bold text-white" onClick={() => nav("/")} data-testid="book-trip-top-button">
              <BusFront className="h-4 w-4" /><span>Book a trip</span>
            </Button>
          )}

          {isStaffArea && user && (
            <span className="hidden items-center gap-2 whitespace-nowrap pr-1 text-sm font-semibold text-slate-700 lg:inline-flex" data-testid="staff-identity">
              <UserRound className="h-4 w-4 text-[#0052FF]" />{user.name}
              {terminal && <span className="chip max-w-[200px] truncate bg-blue-50 text-[#0052FF]" data-testid="header-terminal">{terminal.city} · {terminal.name}</span>}
            </span>
          )}

          {user ? (
            <>
              <Button variant="outline" size="sm" className="rounded-full btn-press" onClick={() => { signOut(); nav(logoutDest); }} data-testid="logout-button">
                <LogOut className="h-4 w-4" /><span className="hidden sm:inline">Logout</span>
              </Button>
            </>
          ) : !isStaffArea ? (
            <Button variant="outline" size="sm" className="rounded-full btn-press px-3 font-semibold sm:px-4" onClick={() => nav("/login")} data-testid="login-nav-button">
              <UserRound className="h-4 w-4 sm:hidden" /><span className="hidden sm:inline">Login</span>
            </Button>
          ) : null}

          {items.length > 0 && <MenuSheet items={items} user={user} terminal={terminal} onChangePassword={user ? () => setPwOpen(true) : null} always />}
        </nav>
      </div>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </header>
  );
}
